import {useBooleanState} from '@wandb/weave/common/util/hooks';
// @todo: Migrate to @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {Button} from '@wandb/weave/components/Button';
import {groupBy, kebabCase} from 'lodash';
import React from 'react';
import {twMerge} from 'tailwind-merge';

import {trackAddPanels} from '../../../services/analytics/addPanelsEvents';
import {SectionSettingsContextProvider} from '../Settings/SectionSettingsContextProvider';
import {useWorkspaceDrawerContext} from '../WorkspaceDrawerContextProvider';
import {AddPanelsMenuItem} from './AddPanelsMenuItem';
import {IconColorContextProvider} from './contexts/IconColorContext';
import {NewPanelEditorContextProvider} from './contexts/NewPanelEditorContext';
import type {AddPanelsMenuGroupProps, AddPanelsMenuItemProps} from './types';

export const AddPanelsMenuGroup = (props: AddPanelsMenuGroupProps) => {
  const {sectionRef} = props;
  if (sectionRef) {
    return (
      <SectionSettingsContextProvider sectionRef={sectionRef}>
        <AddPanelsMenuGroupInner {...props} />
      </SectionSettingsContextProvider>
    );
  }
  return <AddPanelsMenuGroupInner {...props} />;
};

const AddPanelsMenuGroupInner = ({
  title,
  description,
  iconColor,
  items,
  isExpandedDefault = false,
}: AddPanelsMenuGroupProps) => {
  const {targetSectionRef} = useWorkspaceDrawerContext();
  const analyticsScope = !targetSectionRef ? 'global' : 'section';

  const {
    itemsAboveShowMore = [],
    itemsHiddenByDefault = [],
    disabledItems = [],
  } = groupBy(items, item => {
    if (item.isDisabled) {
      return 'disabledItems';
    }
    if (item.isHiddenDefault) {
      return 'itemsHiddenByDefault';
    }
    return 'itemsAboveShowMore';
  });
  const itemsBelowShowMore = itemsHiddenByDefault.concat(disabledItems);

  const {
    state: isShowingMore,
    setTrue: showMore,
    setFalse: showLess,
  } = useBooleanState(false);
  const isShowMoreAllowed =
    itemsAboveShowMore.length > 0 &&
    itemsBelowShowMore.length > 0 &&
    !isShowingMore;

  const {state: isExpanded, toggle: toggleIsExpanded} = useBooleanState(
    !title || (isExpandedDefault && itemsAboveShowMore.length > 0)
  );

  const isDescriptionShown = !!description && isExpanded;

  return (
    <NewPanelEditorContextProvider groupTitle={title}>
      <IconColorContextProvider iconColor={iconColor}>
        <div
          role="group"
          className={twMerge(
            'p-8 last:mb-[80px]',
            !title && 'py-12',
            !isExpanded && 'last:border-b last:border-b-moon-250'
          )}>
          {title != null && (
            <div
              data-test={`add-panels-group-toggle-${kebabCase(title)}`}
              className={twMerge(
                'flex items-center justify-between px-16 py-6',
                '[&:hover_*]:text-moon-600',
                isDescriptionShown && 'pb-0'
              )}
              role="button"
              onClick={() => {
                toggleIsExpanded();
                showLess();
                trackAddPanels({
                  action: isExpanded ? 'Group collapsed' : 'Group expanded',
                  scope: analyticsScope,
                  group: title,
                });
              }}>
              <p className="text-[15px] font-semibold uppercase tracking-wide text-moon-500">
                {title}
              </p>
              <Button
                variant="ghost"
                icon={isExpanded ? 'chevron-up' : 'chevron-down'}
                aria-label={isExpanded ? 'collapse group' : 'expand group'}
              />
            </div>
          )}
          {isExpanded && (
            <>
              {isDescriptionShown && (
                <p className="px-16 pb-8 text-sm text-moon-500">
                  {description}
                </p>
              )}
              {itemsAboveShowMore.length > 0 ? (
                <>
                  <ItemsList items={itemsAboveShowMore} />
                  {isShowMoreAllowed && (
                    <TextButton
                      onClick={() => {
                        showMore();
                        trackAddPanels({
                          action: 'Show more clicked',
                          scope: analyticsScope,
                          group: title,
                        });
                      }}>
                      More
                    </TextButton>
                  )}
                  {isShowingMore && (
                    <>
                      <ItemsList items={itemsBelowShowMore} />
                      <TextButton
                        onClick={() => {
                          showLess();
                          trackAddPanels({
                            action: 'Show less clicked',
                            scope: analyticsScope,
                            group: title,
                          });
                        }}>
                        Less
                      </TextButton>
                    </>
                  )}
                </>
              ) : (
                <ItemsList items={itemsBelowShowMore} />
              )}
            </>
          )}
        </div>
      </IconColorContextProvider>
    </NewPanelEditorContextProvider>
  );
};

const ItemsList = ({items}: {items: AddPanelsMenuItemProps[]}) => (
  <>
    {items.map(item => (
      <AddPanelsMenuItem key={item.type} {...item} />
    ))}
  </>
);

const TextButton = ({
  onClick,
  children,
}: React.ButtonHTMLAttributes<HTMLButtonElement>) => (
  <button
    data-test="add-panels-text-button"
    className="ml-[56px] px-12 py-8 font-semibold text-teal-600 hover:text-teal-550"
    onClick={onClick}>
    {children}
  </button>
);
